import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useNavigate } from "react-router-dom";
import { useAgency } from "@/contexts/AgencyContext";
import { supabase } from "@/lib/supabase";

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const { agency } = useAgency();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [validatingToken, setValidatingToken] = useState(true);

  useEffect(() => {
    const validateRecoveryToken = async () => {
      try {
        if (!supabase) {
          setError("Sistema não configurado corretamente");
          setValidatingToken(false);
          return;
        }

        const { data, error: sessionError } = await supabase.auth.getSession();

        if (sessionError || !data.session) {
          console.error("Token de recuperação inválido:", sessionError);
          setError("Link de recuperação inválido ou expirado");
          setValidatingToken(false);
          return;
        }

        console.log("✅ Token de recuperação válido");
        setValidatingToken(false);
      } catch (err) {
        console.error("Erro ao validar token:", err);
        setError("Erro ao validar link de recuperação");
        setValidatingToken(false);
      }
    };

    validateRecoveryToken();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!password || !confirmPassword) {
      setError("Por favor, preencha todos os campos");
      return;
    }

    if (password.length < 6) {
      setError("A senha deve ter no mínimo 6 caracteres");
      return;
    }

    if (password !== confirmPassword) {
      setError("As senhas não coincidem");
      return;
    }

    setLoading(true);

    try {
      if (!supabase) {
        setError("Sistema não configurado corretamente");
        return;
      }

      const { error: updateError } = await supabase.auth.updateUser({
        password: password,
      });

      if (updateError) {
        console.error("Erro ao atualizar senha:", updateError);
        setError("Erro ao redefinir senha. Tente novamente.");
        return;
      }

      console.log("✅ Senha atualizada com sucesso");
      setSuccess(true);

      setTimeout(() => {
        navigate("/login");
      }, 3000);
    } catch (err: any) {
      console.error("Erro ao redefinir senha:", err);
      setError("Erro ao redefinir senha. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  if (validatingToken) {
    return (
      <div className="w-full h-full flex justify-center items-center relative">
        <img
          src="/login-gradient.png"
          className="absolute top-0 left-0 w-full h-full -z-10"
          alt=""
        />
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-neutral-600 border-t-neutral-50 rounded-full animate-spin"></div>
          <p className="text-neutral-50 text-lg">Validando link...</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="w-full h-full flex justify-center items-center relative">
        <img
          src="/login-gradient.png"
          className="absolute top-0 left-0 w-full h-full -z-10"
          alt=""
        />
        <div className="w-full gap-4 flex flex-col max-w-[425px] text-neutral-50">
          <div className="w-full gap-6 flex flex-col bg-neutral-600 rounded-md p-6">
            <img
              src={agency?.logo_url || "/logo-white.png"}
              className="h-[57px] max-w-[180px] self-center object-contain"
              alt={agency?.company_name || "Logo"}
              onError={(e) => {
                e.currentTarget.src = "/logo-white.png";
              }}
            />
            <div className="p-4 bg-green-500/20 border border-green-500/30 rounded-md">
              <p className="text-green-300 text-sm text-center">
                Senha redefinida com sucesso!
              </p>
              <p className="text-neutral-300 text-xs text-center mt-2">
                Você será redirecionado para o login em instantes...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error && validatingToken === false && !password) {
    return (
      <div className="w-full h-full flex justify-center items-center relative">
        <img
          src="/login-gradient.png"
          className="absolute top-0 left-0 w-full h-full -z-10"
          alt=""
        />
        <div className="w-full gap-4 flex flex-col max-w-[425px] text-neutral-50">
          <div className="w-full gap-6 flex flex-col bg-neutral-600 rounded-md p-6">
            <img
              src={agency?.logo_url || "/logo-white.png"}
              className="h-[57px] max-w-[180px] self-center object-contain"
              alt={agency?.company_name || "Logo"}
              onError={(e) => {
                e.currentTarget.src = "/logo-white.png";
              }}
            />
            <div className="p-4 bg-red-500/20 border border-red-500/30 rounded-md">
              <p className="text-red-300 text-sm text-center">{error}</p>
            </div>
            <Button onClick={() => navigate("/login")} variant="primary">
              Voltar para o login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex justify-center items-center relative">
      <img
        src="/login-gradient.png"
        className="absolute top-0 left-0 w-full h-full -z-10"
        alt=""
      />
      <div className="w-full gap-4 flex flex-col max-w-[425px] text-neutral-50">
        <div className="w-full gap-6 flex flex-col bg-neutral-600 rounded-md p-6">
          <img
            src={agency?.logo_url || "/logo-white.png"}
            className="h-[57px] max-w-[180px] self-center object-contain"
            alt={agency?.company_name || "Logo"}
            onError={(e) => {
              e.currentTarget.src = "/logo-white.png";
            }}
          />
          <div>
            <h2 className="text-xl font-semibold mb-2">
              Redefinir sua senha
            </h2>
            <p className="text-sm text-neutral-300">
              Digite sua nova senha abaixo.
            </p>
          </div>
          {error && (
            <div className="p-3 bg-red-500/20 border border-red-500/30 rounded-md">
              <p className="text-red-300 text-sm">{error}</p>
            </div>
          )}
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="form-group flex flex-col gap-2">
              <label htmlFor="password" className="text-sm">
                Nova senha
              </label>
              <Input
                type="password"
                id="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <div className="form-group flex flex-col gap-2">
              <label htmlFor="confirmPassword" className="text-sm">
                Confirmar nova senha
              </label>
              <Input
                type="password"
                id="confirmPassword"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>
            <Button type="submit" className="mt-4" disabled={loading}>
              {loading ? "Redefinindo..." : "Redefinir senha"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
